import { cn } from "@/lib/utils";

export interface ResponsiveStaticImageProps
  extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, "srcSet"> {
  /** URL de l'image (JPEG/PNG). */
  src: string;
  /** URL de la version WebP (optionnel). Si fourni, utilise <picture> avec source WebP. */
  webpSrc?: string;
  /**
   * Attribut sizes pour le chargement adaptatif (ex: "(max-width: 768px) 100vw, 80vw").
   * Indique au navigateur la largeur d'affichage pour optimiser le chargement.
   */
  sizes?: string;
  /** Classe appliquée à l'élément img (et au picture si utilisé). */
  className?: string;
  /** Largeur intrinsèque (évite CLS). */
  width?: number;
  /** Hauteur intrinsèque (évite CLS). */
  height?: number;
}

/**
 * Image statique avec support optionnel WebP et sizes pour un chargement plus léger et réactif.
 * Utiliser pour hero, destinations et autres images statiques du dossier public.
 */
export const ResponsiveStaticImage = ({
  src,
  alt,
  webpSrc,
  sizes,
  className,
  width,
  height,
  loading = "eager",
  decoding = "async",
  ...props
}: ResponsiveStaticImageProps) => {
  const imgNode = (
    <img
      src={src}
      alt={alt}
      className={cn("w-full h-full object-cover", className)}
      loading={loading}
      decoding={decoding}
      sizes={sizes}
      {...(width != null && height != null && { width, height })}
      {...props}
    />
  );

  if (webpSrc) {
    return (
      <picture className={cn("block w-full h-full", className)}>
        <source type="image/webp" srcSet={webpSrc} sizes={sizes} />
        {imgNode}
      </picture>
    );
  }

  return imgNode;
};
